import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStaffAuth } from '../context/StaffAuthContext';
import { 
  ArrowLeft, Clock, Calendar, Briefcase, TrendingUp,
  Loader2, ChevronDown, ChevronUp, Download, FileText, Filter
} from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StaffWorkHours = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, loading: authLoading, staff } = useStaffAuth();
  
  const [workHours, setWorkHours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDates, setExpandedDates] = useState({});
  const [generating, setGenerating] = useState(false);
  const [periodFilter, setPeriodFilter] = useState('all'); // 'all', 'week', 'month'

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/staff/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const fetchWorkHours = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/staff/work-hours`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkHours(response.data);
    } catch (error) {
      console.error('Error fetching work hours:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchWorkHours();
    }
  }, [token, fetchWorkHours]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !token)) {
      window.location.href = '/staff/login';
    }
  }, [authLoading, isAuthenticated, token]);

  const formatTime = (seconds) => {
    if (!seconds) return '0h 0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'unknown') return 'Unknown Date';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IE', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Filter work hours by period
  const filteredWorkHours = useMemo(() => {
    if (!workHours?.work_hours_by_date) return null;
    
    const now = new Date();
    let startDate;
    
    if (periodFilter === 'week') {
      startDate = new Date(now);
      startDate.setDate(now.getDate() - 7);
    } else if (periodFilter === 'month') {
      startDate = new Date(now);
      startDate.setMonth(now.getMonth() - 1);
    } else {
      // Return all data
      return workHours;
    }
    
    const filteredDates = {};
    let totalSeconds = 0;
    let totalJobs = 0;
    
    Object.entries(workHours.work_hours_by_date).forEach(([date, data]) => {
      const jobDate = new Date(date);
      if (jobDate >= startDate) {
        filteredDates[date] = data;
        totalSeconds += data.total_seconds;
        totalJobs += data.jobs.length;
      }
    });
    
    return {
      work_hours_by_date: filteredDates,
      total_seconds: totalSeconds,
      total_jobs: totalJobs
    };
  }, [workHours, periodFilter]);

  const getPeriodLabel = () => {
    switch (periodFilter) {
      case 'week': return 'Last 7 Days';
      case 'month': return 'Last 30 Days';
      default: return 'All Time';
    }
  };

  const toggleExpand = (date) => {
    setExpandedDates(prev => ({
      ...prev,
      [date]: !prev[date]
    }));
  };

  const generatePDF = async () => {
    if (!filteredWorkHours || !filteredWorkHours.work_hours_by_date) return;
    
    setGenerating(true);
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Load logo image
      const logoUrl = 'https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/l9fyih4f_Whisk_4dd066d44f7c3e88b4a47b0cc96820dddr-removebg-preview.png';
      
      // Create image element to load the logo
      const loadImage = (url) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/jpeg'));
          };
          img.onerror = reject;
          img.src = url;
        });
      };
      
      // Header with company branding
      doc.setFillColor(26, 92, 58); // #1a5c3a
      doc.rect(0, 0, pageWidth, 45, 'F');
      
      // Try to add logo
      try {
        const logoData = await loadImage(logoUrl);
        // Add logo centered in header (white logo on green background)
        doc.addImage(logoData, 'JPEG', pageWidth / 2 - 25, 5, 50, 20);
      } catch (imgError) {
        // Fallback to text if logo fails to load
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(22);
        doc.setFont('helvetica', 'bold');
        doc.text('SANCUR', pageWidth / 2, 18, { align: 'center' });
      }
      
      // Report title with period
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Work Hours Report - ${getPeriodLabel()}`, pageWidth / 2, 38, { align: 'center' });
      
      // Staff info section
      doc.setTextColor(51, 51, 51);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Staff Member:', 14, 57);
      doc.setFont('helvetica', 'normal');
      doc.text(staff?.name || 'Staff', 50, 57);
      
      doc.setFont('helvetica', 'bold');
      doc.text('Generated:', 14, 65);
      doc.setFont('helvetica', 'normal');
      doc.text(new Date().toLocaleDateString('en-IE', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }), 50, 65);
      
      // Summary box
      doc.setFillColor(245, 245, 245);
      doc.roundedRect(14, 73, pageWidth - 28, 25, 3, 3, 'F');
      
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(`SUMMARY (${getPeriodLabel()})`, 20, 82);
      
      doc.setFont('helvetica', 'normal');
      const summaryY = 90;
      doc.text(`Total Hours: ${formatTime(filteredWorkHours.total_seconds)}`, 20, summaryY);
      doc.text(`Jobs Completed: ${filteredWorkHours.total_jobs}`, 80, summaryY);
      doc.text(`Days Worked: ${Object.keys(filteredWorkHours.work_hours_by_date).length}`, 140, summaryY);
      
      // Table data
      const tableData = [];
      Object.entries(filteredWorkHours.work_hours_by_date)
        .sort((a, b) => new Date(b[0]) - new Date(a[0]))
        .forEach(([date, data]) => {
          data.jobs.forEach((job, index) => {
            tableData.push([
              index === 0 ? formatDate(date) : '',
              job.service_type,
              job.client_name,
              formatTime(job.seconds)
            ]);
          });
          // Add subtotal row for the day
          tableData.push([
            { content: '', styles: { fillColor: [240, 240, 240] } },
            { content: 'Day Total:', colSpan: 2, styles: { fontStyle: 'bold', halign: 'right', fillColor: [240, 240, 240] } },
            { content: formatTime(data.total_seconds), styles: { fontStyle: 'bold', fillColor: [240, 240, 240] } }
          ]);
        });
      
      // Generate table
      doc.autoTable({
        startY: 105,
        head: [['Date', 'Service Type', 'Client', 'Hours']],
        body: tableData,
        theme: 'striped',
        headStyles: { 
          fillColor: [26, 92, 58],
          textColor: 255,
          fontStyle: 'bold'
        },
        styles: {
          fontSize: 9,
          cellPadding: 4
        },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 50 },
          2: { cellWidth: 50 },
          3: { cellWidth: 30, halign: 'right' }
        }
      });
      
      // Grand total at the end
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFillColor(26, 92, 58);
      doc.roundedRect(14, finalY, pageWidth - 28, 15, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('TOTAL HOURS:', 20, finalY + 10);
      doc.text(formatTime(filteredWorkHours.total_seconds), pageWidth - 20, finalY + 10, { align: 'right' });
      
      // Footer
      const footerY = doc.internal.pageSize.getHeight() - 15;
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text('Sancur Services - Professional Cleaning Solutions', pageWidth / 2, footerY, { align: 'center' });
      doc.text('www.sancurservices.com', pageWidth / 2, footerY + 5, { align: 'center' });
      
      // Save PDF
      const fileName = `work_hours_${staff?.name?.replace(/\s+/g, '_') || 'staff'}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setGenerating(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#1a5c3a] mx-auto mb-4" size={40} />
          <p className="text-gray-500">Loading work hours...</p>
        </div>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated || !token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin text-[#1a5c3a] mx-auto mb-4" size={40} />
          <p className="text-gray-500">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  const totalHours = filteredWorkHours?.total_seconds ? Math.floor(filteredWorkHours.total_seconds / 3600) : 0;
  const totalMinutes = filteredWorkHours?.total_seconds ? Math.floor((filteredWorkHours.total_seconds % 3600) / 60) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1a5c3a] text-white shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/staff/dashboard')}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft size={24} />
              </button>
              <img 
                src="https://customer-assets.emergentagent.com/job_trusted-maintenance-1/artifacts/l9fyih4f_Whisk_4dd066d44f7c3e88b4a47b0cc96820dddr-removebg-preview.png" 
                alt="Sancur Services" 
                className="h-10 w-auto brightness-0 invert"
              />
            </div>
            
            {/* PDF Export Button */}
            {filteredWorkHours?.work_hours_by_date && Object.keys(filteredWorkHours.work_hours_by_date).length > 0 && (
              <button
                onClick={generatePDF}
                disabled={generating}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
                data-testid="export-pdf-btn"
              >
                {generating ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <Download size={18} />
                )}
                <span className="hidden sm:inline">PDF</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Period Filter */}
        <div className="flex items-center gap-2 bg-white rounded-xl p-1 shadow-sm border border-gray-100">
          <button
            onClick={() => setPeriodFilter('week')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              periodFilter === 'week' 
                ? 'bg-[#1a5c3a] text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="filter-week"
          >
            Last 7 Days
          </button>
          <button
            onClick={() => setPeriodFilter('month')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              periodFilter === 'month' 
                ? 'bg-[#1a5c3a] text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="filter-month"
          >
            Last 30 Days
          </button>
          <button
            onClick={() => setPeriodFilter('all')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
              periodFilter === 'all' 
                ? 'bg-[#1a5c3a] text-white' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="filter-all"
          >
            All Time
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-[#1a5c3a]/10 rounded-lg">
                <Clock className="text-[#1a5c3a]" size={24} />
              </div>
              <span className="text-sm text-gray-500">Total Hours</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {totalHours}h {totalMinutes}m
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-[#d4af37]/10 rounded-lg">
                <Briefcase className="text-[#d4af37]" size={24} />
              </div>
              <span className="text-sm text-gray-500">Jobs Completed</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {filteredWorkHours?.total_jobs || 0}
            </p>
          </div>
        </div>

        {/* Work Hours by Day */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Calendar className="text-[#1a5c3a]" size={20} />
              Hours by Day
              <span className="text-xs font-normal text-gray-500 ml-2">({getPeriodLabel()})</span>
            </h2>
          </div>

          {!filteredWorkHours?.work_hours_by_date || Object.keys(filteredWorkHours.work_hours_by_date).length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Clock size={48} className="mx-auto mb-4 text-gray-300" />
              <p>No completed work in this period</p>
              <p className="text-sm mt-2">Try selecting a different time range</p>
            </div>
          ) : (
            <div className="divide-y">
              {Object.entries(filteredWorkHours.work_hours_by_date)
                .sort((a, b) => new Date(b[0]) - new Date(a[0]))
                .map(([date, data]) => (
                <div key={date} className="hover:bg-gray-50">
                  <button
                    onClick={() => toggleExpand(date)}
                    className="w-full p-4 flex items-center justify-between"
                    data-testid={`date-row-${date}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#1a5c3a]/10 rounded-xl flex items-center justify-center">
                        <Calendar className="text-[#1a5c3a]" size={20} />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{formatDate(date)}</p>
                        <p className="text-sm text-gray-500">
                          {data.jobs.length} job{data.jobs.length !== 1 ? 's' : ''} completed
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-lg font-bold text-[#1a5c3a]">
                          {formatTime(data.total_seconds)}
                        </p>
                      </div>
                      {expandedDates[date] ? (
                        <ChevronUp className="text-gray-400" size={20} />
                      ) : (
                        <ChevronDown className="text-gray-400" size={20} />
                      )}
                    </div>
                  </button>
                  
                  {/* Expanded Job Details */}
                  {expandedDates[date] && (
                    <div className="px-4 pb-4">
                      <div className="ml-16 space-y-2">
                        {data.jobs.map((job, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                          >
                            <div>
                              <p className="font-medium text-gray-800">{job.service_type}</p>
                              <p className="text-sm text-gray-500">{job.client_name}</p>
                            </div>
                            <span className="text-sm font-medium text-gray-600">
                              {formatTime(job.seconds)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Weekly Summary */}
        {filteredWorkHours?.work_hours_by_date && Object.keys(filteredWorkHours.work_hours_by_date).length > 0 && (
          <div className="bg-gradient-to-r from-[#1a5c3a] to-[#0f3d26] rounded-2xl p-6 text-white">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp size={24} />
              <h3 className="font-semibold">Performance Summary ({getPeriodLabel()})</h3>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-white/70 text-sm">Average per job</p>
                <p className="text-2xl font-bold">
                  {filteredWorkHours.total_jobs > 0 
                    ? formatTime(Math.floor(filteredWorkHours.total_seconds / filteredWorkHours.total_jobs))
                    : '0h 0m'}
                </p>
              </div>
              <div>
                <p className="text-white/70 text-sm">Days worked</p>
                <p className="text-2xl font-bold">
                  {Object.keys(filteredWorkHours.work_hours_by_date).length}
                </p>
              </div>
            </div>
            
            {/* Export PDF Button */}
            <button
              onClick={generatePDF}
              disabled={generating}
              className="w-full flex items-center justify-center gap-3 py-3 bg-white/20 hover:bg-white/30 rounded-xl transition-colors disabled:opacity-50"
              data-testid="export-pdf-btn-large"
            >
              {generating ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  <span>Generating PDF...</span>
                </>
              ) : (
                <>
                  <FileText size={20} />
                  <span>Download Report ({getPeriodLabel()})</span>
                </>
              )}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default StaffWorkHours;
